import React from 'react';
import { CircleHelp, FileSignature, Upload, X } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { DocumentFolderSidebar } from '../components/DocumentFolderSidebar';
import { TemplatesList, type TemplateItem } from '../features/templates/components/TemplatesList';
import { BusinessDocumentsSection } from '../features/business-documents/components/BusinessDocumentsSection';
import { TemplateFillModal } from '../features/templates/components/TemplateFillModal';
import { PdfPreviewModal } from '../features/templates/components/PdfPreviewModal';
import { TemplatePlaceholdersHelpModal } from '../features/templates/components/TemplatePlaceholdersHelpModal';
import { TemplateSignwellSendModal, type TemplateSignwellInboxAccount } from '../features/templates/components/TemplateSignwellSendModal';
import {
  TemplateSignatureRequestsList,
  type TemplateSignatureRequestItem,
} from '../features/templates/components/TemplateSignatureRequestsList';
import { SignwellRequestingEmbed } from '../features/templates/components/SignwellRequestingEmbed';
import { MAX_FOLDER_NAME_LENGTH, folderNameTooLongMessage } from '@/constants/folderName';
import { useCapabilities } from '../hooks/useCapabilities';

type TemplatesResponse = {
  templates?: TemplateItem[];
  hasMore?: boolean;
  nextCursor?: string | null;
};

type BusinessDocumentFolder = {
  id: string;
  name: string;
  document_count: number;
};

type TemplatesPageTab = 'templates' | 'signatures' | 'business_documents';

async function parseErrorResponse(res: Response, fallback: string) {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (HTTP ${res.status})`;
  try {
    const parsed = JSON.parse(text) as { error?: string; details?: string };
    if (parsed.details) return `${parsed.error || fallback} (${parsed.details})`;
    return parsed.error || fallback;
  } catch {
    return text;
  }
}

export function TemplatesPage() {
  const [templates, setTemplates] = React.useState<TemplateItem[]>([]);
  const [manualFolders, setManualFolders] = React.useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [deletingTemplateId, setDeletingTemplateId] = React.useState<string | null>(null);
  const [downloadingTemplateId, setDownloadingTemplateId] = React.useState<string | null>(null);
  const [folderModalOpen, setFolderModalOpen] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [folderModalError, setFolderModalError] = React.useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editCategory, setEditCategory] = React.useState('');
  const [savingEditTemplateId, setSavingEditTemplateId] = React.useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = React.useState<TemplateItem | null>(null);
  const [placeholders, setPlaceholders] = React.useState<string[]>([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = React.useState(false);
  const [placeholderError, setPlaceholderError] = React.useState<string | null>(null);
  const [rendering, setRendering] = React.useState(false);
  const [preview, setPreview] = React.useState<{ url: string; fileName: string } | null>(null);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = React.useState<TemplateItem | null>(null);
  const [placeholdersHelpOpen, setPlaceholdersHelpOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<TemplatesPageTab>('templates');
  // Geschäftsunterlagen hängen am Rechnungsmodul (API /business-documents).
  const { can: canCapability } = useCapabilities();
  const showBusinessDocuments = canCapability('web.rechnungen.view');
  React.useEffect(() => {
    if (!showBusinessDocuments && activeTab === 'business_documents') setActiveTab('templates');
  }, [showBusinessDocuments, activeTab]);
  const [businessFolders, setBusinessFolders] = React.useState<BusinessDocumentFolder[]>([]);
  const [businessTotalCount, setBusinessTotalCount] = React.useState(0);
  const [selectedBusinessFolderId, setSelectedBusinessFolderId] = React.useState<string | null>(null);
  const [folderModalSubmitting, setFolderModalSubmitting] = React.useState(false);
  const [signFields, setSignFields] = React.useState<string[]>([]);
  const [lastFillValues, setLastFillValues] = React.useState<Record<string, string>>({});
  const [previewTemplate, setPreviewTemplate] = React.useState<TemplateItem | null>(null);
  const [signSendOpen, setSignSendOpen] = React.useState(false);
  const [signSending, setSignSending] = React.useState(false);
  const [signSendError, setSignSendError] = React.useState<string | null>(null);
  const [signInboxAccounts, setSignInboxAccounts] = React.useState<TemplateSignwellInboxAccount[]>([]);
  const [signInboxAccountsLoading, setSignInboxAccountsLoading] = React.useState(false);
  const [signInboxAccountsError, setSignInboxAccountsError] = React.useState<string | null>(null);
  const [signPreferredInboxAccountId, setSignPreferredInboxAccountId] = React.useState<string | null>(null);
  const [signatureRequests, setSignatureRequests] = React.useState<TemplateSignatureRequestItem[]>([]);
  const [signatureRequestsLoading, setSignatureRequestsLoading] = React.useState(false);
  const [signatureRequestsError, setSignatureRequestsError] = React.useState<string | null>(null);
  const [downloadingSignatureRequestId, setDownloadingSignatureRequestId] = React.useState<string | null>(null);
  const [manualPdfDraftLoading, setManualPdfDraftLoading] = React.useState(false);
  const [manualPdfEditorOpen, setManualPdfEditorOpen] = React.useState(false);
  const [manualPdfUploadError, setManualPdfUploadError] = React.useState<string | null>(null);
  const [manualPdfContinueError, setManualPdfContinueError] = React.useState<string | null>(null);
  const [manualPdfContinuing, setManualPdfContinuing] = React.useState(false);
  const [manualPdfDocumentName, setManualPdfDocumentName] = React.useState('Dokument');
  const [manualPdfRecipientName, setManualPdfRecipientName] = React.useState('');
  const [manualPdfRecipientEmail, setManualPdfRecipientEmail] = React.useState('');
  const [signwellDraftId, setSignwellDraftId] = React.useState<string | null>(null);
  const [signwellEmbeddedEditUrl, setSignwellEmbeddedEditUrl] = React.useState<string | null>(null);
  const [signSendMode, setSignSendMode] = React.useState<'template' | 'manual_pdf'>('template');
  const [manualPdfSendAttempted, setManualPdfSendAttempted] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const businessFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const manualPdfInputRef = React.useRef<HTMLInputElement | null>(null);

  const loadTemplates = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/templates');
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Vorlagen konnten nicht geladen werden.'));
      const data = (await res.json()) as TemplatesResponse;
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Vorlagen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const loadSignatureRequests = React.useCallback(async () => {
    setSignatureRequestsLoading(true);
    setSignatureRequestsError(null);
    try {
      const res = await fetch('/api/templates/signwell/requests');
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Unterschriften konnten nicht geladen werden.'));
      const data = (await res.json()) as { requests?: TemplateSignatureRequestItem[] };
      setSignatureRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (e) {
      setSignatureRequestsError(e instanceof Error ? e.message : 'Unterschriften konnten nicht geladen werden.');
    } finally {
      setSignatureRequestsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 'signatures') void loadSignatureRequests();
  }, [activeTab, loadSignatureRequests]);

  const loadBusinessFolders = React.useCallback(async () => {
    try {
      const [foldersRes, docsRes] = await Promise.all([
        fetch('/api/business-document-folders'),
        fetch('/api/business-documents'),
      ]);
      if (!foldersRes.ok) throw new Error(await parseErrorResponse(foldersRes, 'Ordner konnten nicht geladen werden.'));
      if (!docsRes.ok) throw new Error(await parseErrorResponse(docsRes, 'Dokumente konnten nicht geladen werden.'));
      const foldersData = (await foldersRes.json()) as BusinessDocumentFolder[];
      const docsData = (await docsRes.json()) as unknown[];
      setBusinessFolders(Array.isArray(foldersData) ? foldersData : []);
      setBusinessTotalCount(Array.isArray(docsData) ? docsData.length : 0);
    } catch {
      setBusinessFolders([]);
      setBusinessTotalCount(0);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 'business_documents') void loadBusinessFolders();
  }, [activeTab, loadBusinessFolders]);

  React.useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const folders = React.useMemo(() => {
    const fromTemplates = templates.map((t) => (t.category || '').trim()).filter(Boolean);
    const all = new Set<string>([...manualFolders, ...fromTemplates]);
    return Array.from(all).sort((a, b) => a.localeCompare(b));
  }, [templates, manualFolders]);

  const folderCountMap = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const folder of folders) counts.set(folder, 0);
    for (const template of templates) {
      const category = (template.category || '').trim();
      if (!category) continue;
      counts.set(category, (counts.get(category) || 0) + 1);
    }
    return counts;
  }, [folders, templates]);

  const filteredTemplates = React.useMemo(() => {
    if (!selectedFolder) return templates;
    return templates.filter((t) => (t.category || '').trim() === selectedFolder);
  }, [templates, selectedFolder]);

  const templateFolderItems = React.useMemo(
    () => folders.map((folder) => ({ id: folder, name: folder, count: folderCountMap.get(folder) ?? 0 })),
    [folders, folderCountMap],
  );

  const businessFolderItems = React.useMemo(
    () => businessFolders.map((folder) => ({
      id: folder.id,
      name: folder.name,
      count: folder.document_count,
    })),
    [businessFolders],
  );

  const selectedBusinessFolderName = React.useMemo(() => {
    if (!selectedBusinessFolderId) return null;
    return businessFolders.find((f) => f.id === selectedBusinessFolderId)?.name ?? null;
  }, [businessFolders, selectedBusinessFolderId]);

  const showFolderSidebar = activeTab === 'templates' || activeTab === 'business_documents';

  const onUploadClick = () => fileInputRef.current?.click();
  const onBusinessUploadClick = () => businessFileInputRef.current?.click();

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setUploadError('Bitte nur Word-Vorlagen hochladen.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.docx$/i, ''));
      if (selectedFolder) formData.append('category', selectedFolder);
      const res = await fetch('/api/templates/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Upload fehlgeschlagen.'));
      await loadTemplates();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  const addFolder = () => {
    setFolderModalError(null);
    setNewFolderName('');
    setFolderModalOpen(true);
  };

  const closeFolderModal = () => {
    setFolderModalOpen(false);
    setNewFolderName('');
    setFolderModalError(null);
  };

  const submitNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    if (name.length > MAX_FOLDER_NAME_LENGTH) {
      setFolderModalError(folderNameTooLongMessage);
      return;
    }

    if (activeTab === 'business_documents') {
      const exists = businessFolders.some((f) => f.name.toLowerCase() === name.toLowerCase());
      if (exists) {
        setFolderModalError('Ordner existiert bereits.');
        return;
      }
      setFolderModalSubmitting(true);
      setFolderModalError(null);
      try {
        const res = await fetch('/api/business-document-folders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        if (!res.ok) throw new Error(await parseErrorResponse(res, 'Ordner konnte nicht erstellt werden.'));
        const row = (await res.json()) as BusinessDocumentFolder;
        await loadBusinessFolders();
        setSelectedBusinessFolderId(row.id);
        closeFolderModal();
      } catch (e) {
        setFolderModalError(e instanceof Error ? e.message : 'Ordner konnte nicht erstellt werden.');
      } finally {
        setFolderModalSubmitting(false);
      }
      return;
    }

    const exists = manualFolders.some((f) => f.toLowerCase() === name.toLowerCase())
      || templates.some((t) => String(t.category || '').trim().toLowerCase() === name.toLowerCase());
    if (exists) {
      setFolderModalError('Ordner existiert bereits.');
      return;
    }
    setManualFolders((prev) => [...prev, name]);
    setSelectedFolder(name);
    closeFolderModal();
  };

  const startEditTemplate = (template: TemplateItem) => {
    setEditingTemplateId(template.id);
    setEditName(template.name || '');
    setEditCategory((template.category || '').trim());
  };

  const cancelEditTemplate = () => {
    setEditingTemplateId(null);
    setEditName('');
    setEditCategory('');
  };

  const saveEditTemplate = async (template: TemplateItem) => {
    const nextName = editName.trim();
    const nextCategory = editCategory.trim();
    const currentName = (template.name || '').trim();
    const currentCategory = (template.category || '').trim();
    if (!nextName) {
      setUploadError('Dokumentname darf nicht leer sein.');
      return;
    }
    if (nextName === currentName && nextCategory === currentCategory) {
      cancelEditTemplate();
      return;
    }
    setSavingEditTemplateId(template.id);
    try {
      const res = await fetch(`/api/templates/${encodeURIComponent(template.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nextName, category: nextCategory || null, versionId: template.versionId || null }),
      });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Ordner konnte nicht aktualisiert werden.'));
      await loadTemplates();
      cancelEditTemplate();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Dokument konnte nicht aktualisiert werden.');
    } finally {
      setSavingEditTemplateId(null);
    }
  };

  const deleteTemplate = async (template: TemplateItem) => {
    setPendingDeleteTemplate(template);
  };

  const confirmDeleteTemplate = async () => {
    const template = pendingDeleteTemplate;
    if (!template) return;
    setDeletingTemplateId(template.id);
    try {
      const query = template.versionId ? `?versionId=${encodeURIComponent(template.versionId)}` : '';
      const res = await fetch(`/api/templates/${encodeURIComponent(template.id)}${query}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Dokument konnte nicht gelöscht werden.'));
      await loadTemplates();
      setPendingDeleteTemplate(null);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Dokument konnte nicht gelöscht werden.');
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const openTemplate = async (template: TemplateItem) => {
    setSelectedTemplate(template);
    setPlaceholders([]);
    setSignFields([]);
    setPlaceholderError(null);
    setLoadingPlaceholders(true);
    try {
      const res = await fetch(`/api/templates/${encodeURIComponent(template.id)}/placeholders`);
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Platzhalter konnten nicht geladen werden.'));
      const data = (await res.json()) as { placeholders?: string[]; signFields?: string[] };
      const nextPlaceholders = Array.isArray(data.placeholders) ? data.placeholders : [];
      const nextSignFields = Array.isArray(data.signFields) ? data.signFields : [];
      setPlaceholders(nextPlaceholders);
      setSignFields(nextSignFields);
      if (nextSignFields.length) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === template.id ? { ...t, hasSignFields: true } : t)),
        );
      }
    } catch (e) {
      setPlaceholderError(e instanceof Error ? e.message : 'Platzhalter konnten nicht geladen werden.');
    } finally {
      setLoadingPlaceholders(false);
    }
  };

  const downloadTemplate = async (template: TemplateItem) => {
    setDownloadingTemplateId(template.id);
    try {
      const res = await fetch(`/api/templates/${encodeURIComponent(template.id)}/download`);
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Vorlage konnte nicht heruntergeladen werden.'));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const fileNameBase = (template.name || 'vorlage').replace(/[^\w.-]+/g, '_');
      anchor.href = url;
      anchor.download = fileNameBase.toLowerCase().endsWith('.docx') ? fileNameBase : `${fileNameBase}.docx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Vorlage konnte nicht heruntergeladen werden.');
    } finally {
      setDownloadingTemplateId(null);
    }
  };

  const closeFillModal = () => {
    if (rendering) return;
    setSelectedTemplate(null);
    setPlaceholderError(null);
  };

  const createPdf = async (values: Record<string, string>) => {
    if (!selectedTemplate) return;
    setRendering(true);
    setLastFillValues(values);
    try {
      const res = await fetch(`/api/templates/${encodeURIComponent(selectedTemplate.id)}/render-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ values }),
      });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'PDF konnte nicht erstellt werden.'));
      const blob = await res.blob();
      const nextUrl = URL.createObjectURL(blob);
      if (preview?.url) URL.revokeObjectURL(preview.url);
      const fileNameBase = (selectedTemplate.name || 'dokument').replace(/[^\w.-]+/g, '_');
      setPreviewTemplate(selectedTemplate);
      setPreview({ url: nextUrl, fileName: `${fileNameBase || 'dokument'}.pdf` });
      setSelectedTemplate(null);
    } catch (e) {
      setPlaceholderError(e instanceof Error ? e.message : 'PDF konnte nicht erstellt werden.');
    } finally {
      setRendering(false);
    }
  };

  const openSignSendModal = () => {
    setSignSendMode('template');
    setSignSendError(null);
    setSignInboxAccountsError(null);
    setSignInboxAccountsLoading(true);
    setSignPreferredInboxAccountId(null);
    setSignSendOpen(true);
  };

  React.useEffect(() => {
    if (!signSendOpen) return;
    let cancelled = false;
    setSignInboxAccountsLoading(true);
    setSignInboxAccountsError(null);
    setSignPreferredInboxAccountId(null);

    const accountsPromise = fetch('/api/inbox/accounts')
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseErrorResponse(res, 'Postfächer konnten nicht geladen werden.'));
        return res.json() as Promise<Array<{ id?: string; email?: string; nylas_email?: string | null; nylas_grant_id?: string | null }>>;
      })
      .then((accounts) => {
        const sendable = (Array.isArray(accounts) ? accounts : [])
          .filter((a) => Boolean(a?.nylas_grant_id) && Boolean(a?.id))
          .map((a) => ({
            id: String(a.id),
            email: String(a.nylas_email ?? a.email ?? '').trim() || String(a.email ?? ''),
          }))
          .filter((a) => Boolean(a.email));
        return sendable;
      });

    const preferredPromise = fetch('/api/settings/mail-dispatch', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = (await res.json()) as { settings?: { default?: string | null } };
        return String(data?.settings?.default ?? '').trim() || null;
      })
      .catch(() => null);

    void Promise.all([accountsPromise, preferredPromise])
      .then(([sendable, preferred]) => {
        if (cancelled) return;
        setSignInboxAccounts(sendable);
        setSignPreferredInboxAccountId(preferred);
        if (sendable.length === 0) {
          setSignInboxAccountsError('Kein verbundenes Nylas-Postfach. Bitte unter Einstellungen ein Postfach verbinden.');
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setSignInboxAccounts([]);
        setSignPreferredInboxAccountId(null);
        setSignInboxAccountsError(e instanceof Error ? e.message : 'Postfächer konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setSignInboxAccountsLoading(false);
      });
    return () => { cancelled = true; };
  }, [signSendOpen]);

  const manualPdfDocumentNameDisplay = manualPdfDocumentName;
  const manualPdfRecipientEmailNormalized = manualPdfRecipientEmail.trim().toLowerCase();
  const manualPdfRecipientReady =
    Boolean(manualPdfRecipientName.trim()) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualPdfRecipientEmailNormalized);
  const manualPdfUploadDisabled =
    manualPdfDraftLoading || manualPdfEditorOpen || !manualPdfRecipientReady;

  const onManualPdfUploadClick = () => {
    if (manualPdfUploadDisabled) return;
    manualPdfInputRef.current?.click();
  };

  const resetManualPdfDraft = () => {
    setManualPdfEditorOpen(false);
    setSignwellDraftId(null);
    setSignwellEmbeddedEditUrl(null);
    setManualPdfDocumentName('Dokument');
    setManualPdfContinueError(null);
    setManualPdfContinuing(false);
    setManualPdfSendAttempted(false);
  };

  const onManualPdfFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    const isPdf = picked.type === 'application/pdf' || picked.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setManualPdfUploadError('Bitte eine gültige PDF-Datei wählen.');
      return;
    }
    if (picked.size > 20 * 1024 * 1024) {
      setManualPdfUploadError('Die PDF darf maximal 20 MB groß sein.');
      return;
    }
    const recipientName = manualPdfRecipientName.trim();
    const recipientEmail = manualPdfRecipientEmail.trim().toLowerCase();
    if (!recipientName || !recipientEmail) {
      setManualPdfUploadError('Bitte zuerst Name und E-Mail des Unterzeichners eintragen.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      setManualPdfUploadError('Bitte eine gültige E-Mail-Adresse eintragen.');
      return;
    }
    setManualPdfUploadError(null);
    setManualPdfDraftLoading(true);
    const documentName = picked.name.replace(/\.pdf$/i, '') || 'Dokument';
    const form = new FormData();
    form.append('file', picked);
    form.append('documentName', documentName);
    form.append('recipientName', recipientName);
    form.append('recipientEmail', recipientEmail);
    void fetch('/api/templates/signwell/manual-pdf/draft', { method: 'POST', body: form })
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseErrorResponse(res, 'PDF konnte nicht vorbereitet werden.'));
        return res.json() as Promise<{
          signwellDocumentId?: string;
          embeddedEditUrl?: string;
          documentName?: string;
        }>;
      })
      .then((data) => {
        const id = String(data.signwellDocumentId ?? '').trim();
        const url = String(data.embeddedEditUrl ?? '').trim();
        if (!id || !url) throw new Error('SignWell-Entwurf konnte nicht erstellt werden.');
        setSignwellDraftId(id);
        setSignwellEmbeddedEditUrl(url);
        setManualPdfDocumentName(String(data.documentName ?? documentName));
        setManualPdfSendAttempted(false);
        setManualPdfEditorOpen(true);
      })
      .catch((e: unknown) => {
        setManualPdfUploadError(e instanceof Error ? e.message : 'PDF konnte nicht vorbereitet werden.');
      })
      .finally(() => setManualPdfDraftLoading(false));
  };

  const closeManualPdfEditor = () => {
    const draftId = signwellDraftId;
    const shouldDeleteDraft = draftId && !manualPdfSendAttempted;
    resetManualPdfDraft();
    if (shouldDeleteDraft) {
      void fetch(`/api/templates/signwell/manual-pdf/draft/${encodeURIComponent(draftId)}`, {
        method: 'DELETE',
      }).catch(() => undefined);
    }
  };

  const continueManualPdfSignSend = async () => {
    if (!signwellDraftId) return;
    setManualPdfContinuing(true);
    setManualPdfContinueError(null);
    try {
      const res = await fetch(`/api/templates/signwell/manual-pdf/draft/${encodeURIComponent(signwellDraftId)}`);
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Entwurf konnte nicht geprüft werden.'));
      const data = (await res.json()) as { hasSignatureField?: boolean };
      if (!data.hasSignatureField) {
        throw new Error('Mindestens ein Unterschriftsfeld ist erforderlich.');
      }
      setSignSendMode('manual_pdf');
      setSignSendError(null);
      setSignInboxAccountsError(null);
      setSignInboxAccountsLoading(true);
      setSignPreferredInboxAccountId(null);
      setSignSendOpen(true);
    } catch (e) {
      setManualPdfContinueError(e instanceof Error ? e.message : 'Entwurf konnte nicht geprüft werden.');
    } finally {
      setManualPdfContinuing(false);
    }
  };

  const submitSignwellSend = async (payload: {
    inboxAccountId: string;
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => {
    if (signSendMode === 'manual_pdf') {
      if (!signwellDraftId) return;
      setSignSending(true);
      setSignSendError(null);
      try {
        const res = await fetch('/api/templates/signwell/manual-pdf/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            signwellDocumentId: signwellDraftId,
            documentName: manualPdfDocumentName,
            inboxAccountId: payload.inboxAccountId,
            recipient: { name: payload.name, email: payload.email },
            subject: payload.subject || undefined,
            message: payload.message || undefined,
          }),
        });
        setManualPdfSendAttempted(true);
        if (!res.ok) throw new Error(await parseErrorResponse(res, 'Senden fehlgeschlagen.'));
        setSignSendOpen(false);
        resetManualPdfDraft();
        setSignSendMode('template');
        setActiveTab('signatures');
        void loadSignatureRequests();
      } catch (e) {
        setSignSendError(e instanceof Error ? e.message : 'Senden fehlgeschlagen.');
      } finally {
        setSignSending(false);
      }
      return;
    }

    if (!previewTemplate) return;
    setSignSending(true);
    setSignSendError(null);
    try {
      const res = await fetch(`/api/templates/${encodeURIComponent(previewTemplate.id)}/signwell/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          values: lastFillValues,
          inboxAccountId: payload.inboxAccountId,
          recipient: { name: payload.name, email: payload.email },
          subject: payload.subject || undefined,
          message: payload.message || undefined,
        }),
      });
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'Senden fehlgeschlagen.'));
      setSignSendOpen(false);
      setActiveTab('signatures');
      void loadSignatureRequests();
    } catch (e) {
      setSignSendError(e instanceof Error ? e.message : 'Senden fehlgeschlagen.');
    } finally {
      setSignSending(false);
    }
  };

  const openManualPdfResend = (row: TemplateSignatureRequestItem) => {
    const signwellDocumentId = String(row.signwellDocumentId ?? '').trim();
    if (!signwellDocumentId) return;
    setSignwellDraftId(signwellDocumentId);
    setManualPdfDocumentName(row.documentName);
    setManualPdfRecipientName(row.recipientName);
    setManualPdfRecipientEmail(row.recipientEmail);
    setManualPdfSendAttempted(true);
    setSignSendMode('manual_pdf');
    setSignSendError(null);
    setSignInboxAccountsError(null);
    setSignInboxAccountsLoading(true);
    setSignPreferredInboxAccountId(null);
    setSignSendOpen(true);
  };

  const downloadSignedPdf = async (requestId: string) => {
    setDownloadingSignatureRequestId(requestId);
    try {
      const res = await fetch(`/api/templates/signwell/requests/${encodeURIComponent(requestId)}`);
      if (!res.ok) throw new Error(await parseErrorResponse(res, 'PDF konnte nicht geladen werden.'));
      const data = (await res.json()) as { signedPdfUrl?: string | null };
      if (!data.signedPdfUrl) throw new Error('Unterschriebenes PDF ist noch nicht verfügbar.');
      window.open(data.signedPdfUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setSignatureRequestsError(e instanceof Error ? e.message : 'PDF konnte nicht geladen werden.');
    } finally {
      setDownloadingSignatureRequestId(null);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-app-bg font-sans">
      <AppSidebar active="templates" />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Vorlagen" />
        <section className="flex min-h-0 flex-1 flex-col md:flex-row">
          {showFolderSidebar ? (
            activeTab === 'business_documents' ? (
              <DocumentFolderSidebar
                folders={businessFolderItems}
                totalCount={businessTotalCount}
                selectedFolderId={selectedBusinessFolderId}
                onSelectFolder={setSelectedBusinessFolderId}
                onAddFolder={addFolder}
              />
            ) : (
              <DocumentFolderSidebar
                folders={templateFolderItems}
                totalCount={templates.length}
                selectedFolderId={selectedFolder}
                onSelectFolder={setSelectedFolder}
                onAddFolder={addFolder}
                mobileTrailing={(
                  <button
                    type="button"
                    onClick={() => setPlaceholdersHelpOpen(true)}
                    className="shrink-0 p-2 rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text"
                    aria-label="Anleitung"
                    title="Anleitung"
                  >
                    <CircleHelp className="w-4 h-4" />
                  </button>
                )}
              />
            )
          ) : null}

          <div className="app-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-3 md:p-6">
            <div className="mx-auto max-w-7xl space-y-5">
              <div className="flex gap-1 border-b border-app-border">
                <button
                  type="button"
                  onClick={() => setActiveTab('templates')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeTab === 'templates'
                      ? 'border-pipedrive-blue text-pipedrive-blue'
                      : 'border-transparent text-app-text-subtle hover:text-app-text'
                  }`}
                >
                  Vorlagen
                </button>
                {showBusinessDocuments ? (
                <button
                  type="button"
                  onClick={() => setActiveTab('business_documents')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeTab === 'business_documents'
                      ? 'border-pipedrive-blue text-pipedrive-blue'
                      : 'border-transparent text-app-text-subtle hover:text-app-text'
                  }`}
                >
                  Geschäftsunterlagen
                </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setActiveTab('signatures')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeTab === 'signatures'
                      ? 'border-pipedrive-blue text-pipedrive-blue'
                      : 'border-transparent text-app-text-subtle hover:text-app-text'
                  }`}
                >
                  Unterschriften
                </button>
              </div>

              {activeTab === 'templates' ? (
                <>
              {/* Upload card — desktop only; FAB handles uploads on mobile */}
              <div className="hidden md:block rounded-xl border border-app-border bg-app-surface px-5 py-4">
                <div className="flex items-center justify-between gap-8 lg:gap-12">
                  <div className="flex-1 min-w-0 pr-2">
                    <h2 className="text-base font-semibold text-app-text">Vorlagen</h2>
                    <p className="text-sm text-app-text-subtle mt-1 leading-relaxed">
                      Lade Word-Vorlagen hoch, fülle die erkannten Felder aus, erstelle ein PDF und sende es optional
                      zur elektronischen Unterschrift. Signaturfähige Vorlagen brauchen in Word ein Feld wie{' '}
                      <span className="font-mono text-xs">{'{d.unterschrift:sign}'}</span> - Details in der Anleitung.
                    </p>
                    <p className="text-xs text-app-text-subtle mt-3">
                      <span className="text-app-text-subtle">Zielordner:</span>{' '}
                      <span className="font-medium text-app-text-muted">{selectedFolder || 'Kein Ordner'}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setPlaceholdersHelpOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text"
                    >
                      <CircleHelp className="h-4 w-4 text-app-text-subtle" aria-hidden />
                      Anleitung
                    </button>
                    <button
                      type="button"
                      onClick={onUploadClick}
                      disabled={uploading}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {uploading ? (
                        <LoadingSpinner className="shrink-0" />
                      ) : (
                        <Upload className="h-4 w-4 shrink-0" aria-hidden />
                      )}
                      {uploading ? 'Hochladen...' : 'Vorlage hochladen'}
                    </button>
                  </div>
                </div>
              </div>
              {/* Hidden file input — used by both desktop button and mobile FAB */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onFileChange}
                className="hidden"
              />

              {uploadError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {uploadError}
                </div>
              ) : null}

              <TemplatesList
                templates={filteredTemplates}
                folders={folders}
                loading={loading}
                error={error}
                onSelect={(t) => void openTemplate(t)}
                editingTemplateId={editingTemplateId}
                editName={editName}
                editCategory={editCategory}
                setEditName={setEditName}
                setEditCategory={setEditCategory}
                onStartEdit={(t) => startEditTemplate(t)}
                onSaveEdit={(t) => void saveEditTemplate(t)}
                onCancelEdit={cancelEditTemplate}
                savingEditTemplateId={savingEditTemplateId}
                onDelete={(t) => deleteTemplate(t)}
                deletingTemplateId={deletingTemplateId}
                onDownload={(t) => void downloadTemplate(t)}
                downloadingTemplateId={downloadingTemplateId}
              />
                </>
              ) : activeTab === 'signatures' ? (
                <div className="space-y-5">
                  <div className="rounded-xl border border-app-border bg-app-surface px-4 py-3">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
                      <div className="flex items-start gap-3 lg:flex-[2] lg:min-w-[18rem]">
                        <span className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-pipedrive-blue">
                          <FileSignature className="h-5 w-5" aria-hidden />
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-sm font-semibold text-app-text">PDF zur Unterschrift</h2>
                          <p className="text-xs text-app-text-subtle mt-0.5 leading-snug">
                            Unterzeichner eintragen, PDF hochladen, Felder im SignWell-Editor setzen.
                          </p>
                        </div>
                      </div>
                      <div className="w-full lg:flex-[3] grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end min-w-0">
                        <div className="min-w-0">
                          <label htmlFor="manual-pdf-recipient-name" className="block text-xs font-medium text-app-text-muted mb-0.5">
                            Name <span className="text-red-500" aria-hidden>*</span>
                          </label>
                          <input
                            id="manual-pdf-recipient-name"
                            value={manualPdfRecipientName}
                            onChange={(e) => setManualPdfRecipientName(e.target.value)}
                            disabled={manualPdfDraftLoading || manualPdfEditorOpen}
                            placeholder="Max Mustermann"
                            required
                            autoComplete="name"
                            className="w-full border border-app-border rounded-lg px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none disabled:bg-app-surface-muted"
                          />
                        </div>
                        <div className="min-w-0">
                          <label htmlFor="manual-pdf-recipient-email" className="block text-xs font-medium text-app-text-muted mb-0.5">
                            E-Mail <span className="text-red-500" aria-hidden>*</span>
                          </label>
                          <input
                            id="manual-pdf-recipient-email"
                            type="email"
                            value={manualPdfRecipientEmail}
                            onChange={(e) => setManualPdfRecipientEmail(e.target.value)}
                            disabled={manualPdfDraftLoading || manualPdfEditorOpen}
                            placeholder="max@beispiel.de"
                            required
                            autoComplete="email"
                            className="w-full border border-app-border rounded-lg px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none disabled:bg-app-surface-muted"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={onManualPdfUploadClick}
                          disabled={manualPdfUploadDisabled}
                          title={
                            manualPdfRecipientReady
                              ? undefined
                              : 'Bitte zuerst Name und gültige E-Mail eintragen.'
                          }
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-pipedrive-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 shrink-0 disabled:opacity-50 sm:w-auto w-full"
                        >
                          {manualPdfDraftLoading ? (
                            <LoadingSpinner className="shrink-0" />
                          ) : (
                            <Upload className="h-4 w-4 shrink-0" aria-hidden />
                          )}
                          {manualPdfDraftLoading ? 'Lädt…' : 'PDF hochladen'}
                        </button>
                      </div>
                    </div>
                    {manualPdfUploadError ? (
                      <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
                        {manualPdfUploadError}
                      </p>
                    ) : null}
                  </div>
                  <input
                    ref={manualPdfInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={onManualPdfFileChange}
                    className="hidden"
                  />
                  <TemplateSignatureRequestsList
                    requests={signatureRequests}
                    loading={signatureRequestsLoading}
                    error={signatureRequestsError}
                    downloadingId={downloadingSignatureRequestId}
                    onDownload={(id) => void downloadSignedPdf(id)}
                    onResend={openManualPdfResend}
                    onRefresh={() => void loadSignatureRequests()}
                  />
                </div>
              ) : (
                <BusinessDocumentsSection
                  selectedFolderId={selectedBusinessFolderId}
                  selectedFolderName={selectedBusinessFolderName}
                  folders={businessFolders.map((f) => ({ id: f.id, name: f.name }))}
                  onRefreshFolders={loadBusinessFolders}
                  fileInputRef={businessFileInputRef}
                  onUploadClick={onBusinessUploadClick}
                />
              )}
            </div>
          </div>
        </section>
      </main>

      {/* FAB — mobile only */}
      {(activeTab === 'templates' || activeTab === 'business_documents') && (
      <button
        type="button"
        onClick={activeTab === 'business_documents' ? onBusinessUploadClick : onUploadClick}
        disabled={uploading}
        className="fixed bottom-20 right-5 z-30 md:hidden h-14 w-14 rounded-full bg-pipedrive-blue text-white shadow-lg flex items-center justify-center hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50"
        aria-label={activeTab === 'business_documents' ? 'Dokument hochladen' : 'Vorlage hochladen'}
      >
        {uploading ? (
          <LoadingSpinner size="xl" />
        ) : (
          <Upload className="w-6 h-6" />
        )}
      </button>
      )}

      <TemplateFillModal
        open={Boolean(selectedTemplate)}
        templateName={selectedTemplate?.name || selectedTemplate?.id || 'Vorlage'}
        placeholders={placeholders}
        signFields={signFields}
        loadingPlaceholders={loadingPlaceholders}
        loadError={placeholderError}
        submitting={rendering}
        onClose={closeFillModal}
        onSubmit={createPdf}
      />

      <PdfPreviewModal
        open={Boolean(preview)}
        fileUrl={preview?.url || null}
        fileName={preview?.fileName || 'dokument.pdf'}
        canSendForSignature={Boolean(previewTemplate?.hasSignFields || signFields.length > 0)}
        onSendForSignature={openSignSendModal}
        onClose={() =>
          setPreview((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            setPreviewTemplate(null);
            return null;
          })
        }
      />

      <TemplateSignwellSendModal
        open={signSendOpen}
        documentName={
          signSendMode === 'manual_pdf'
            ? manualPdfDocumentNameDisplay
            : (previewTemplate?.name || preview?.fileName || 'Dokument')
        }
        submitting={signSending}
        error={signSendError}
        inboxAccounts={signInboxAccounts}
        inboxAccountsLoading={signInboxAccountsLoading}
        inboxAccountsError={signInboxAccountsError}
        preferredInboxAccountId={signPreferredInboxAccountId}
        initialRecipientName={signSendMode === 'manual_pdf' ? manualPdfRecipientName : ''}
        initialRecipientEmail={signSendMode === 'manual_pdf' ? manualPdfRecipientEmail : ''}
        onClose={() => {
          if (!signSending) {
            setSignSendOpen(false);
            if (signSendMode === 'manual_pdf') setSignSendMode('template');
          }
        }}
        onSubmit={submitSignwellSend}
      />

      <SignwellRequestingEmbed
        open={manualPdfEditorOpen}
        embeddedEditUrl={signwellEmbeddedEditUrl}
        documentName={manualPdfDocumentNameDisplay}
        signwellDocumentId={signwellDraftId}
        continuing={manualPdfContinuing}
        continueError={manualPdfContinueError}
        onClose={closeManualPdfEditor}
        onContinue={() => void continueManualPdfSignSend()}
      />

      <TemplatePlaceholdersHelpModal
        open={placeholdersHelpOpen}
        onClose={() => setPlaceholdersHelpOpen(false)}
      />

      {folderModalOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40"
          onClick={closeFolderModal}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-folder-modal-title"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="new-folder-modal-title" className="text-base font-semibold text-app-text">
                Neuen Ordner hinzufügen
              </h2>
              <button
                type="button"
                onClick={closeFolderModal}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {folderModalError ? (
              <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {folderModalError}
              </p>
            ) : null}

            <label className="block">
              <span className="block text-xs text-app-text-muted mb-1">Ordnername</span>
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submitNewFolder();
                  }
                }}
                className="w-full border border-app-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
                placeholder="z. B. Verträge"
                maxLength={MAX_FOLDER_NAME_LENGTH}
                autoFocus
              />
            </label>
            <p className="mt-2 text-xs text-app-text-subtle">Max. {MAX_FOLDER_NAME_LENGTH} Zeichen.</p>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeFolderModal}
                className="btn-pipedrive-secondary"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void submitNewFolder()}
                disabled={!newFolderName.trim() || folderModalSubmitting}
                className="inline-flex items-center justify-center rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {folderModalSubmitting ? 'Hinzufügen...' : 'Hinzufügen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteTemplate && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/40"
          onClick={() => !deletingTemplateId && setPendingDeleteTemplate(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-template-modal-title"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="delete-template-modal-title" className="text-base font-semibold text-app-text">
                Dokument löschen
              </h2>
              <button
                type="button"
                onClick={() => setPendingDeleteTemplate(null)}
                disabled={Boolean(deletingTemplateId)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-app-text-muted leading-relaxed">
              Möchtest du <span className="font-semibold">{pendingDeleteTemplate.name || pendingDeleteTemplate.id}</span> wirklich löschen?
            </p>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDeleteTemplate(null)}
                disabled={Boolean(deletingTemplateId)}
                className="btn-pipedrive-secondary"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteTemplate()}
                disabled={Boolean(deletingTemplateId)}
                className="app-btn-danger-outline inline-flex items-center justify-center rounded-lg border border-red-200 bg-app-surface px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {deletingTemplateId ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
